import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';
import { locateQuotedSpan } from './clip-segment-resolver';
import { computeSceneGroupShortIds, resolveSceneGroupHandle } from './scene-group-addressing';
import { findWordSequenceIndices, normalizeTranscriptToken } from './transcript-word-match';
import { VideoStateTool } from './VideoStateTool';

import { wordSpanToClipRegion } from 'js/src/data/transcript/word-timing';
import { checkpointVideo } from 'js/src/data/video-data';
import type { RootState } from 'js/src/libs/RootState';
import {
    cutVoicedrivenSceneGroupOperation,
    saveRawLuminary,
    uncutVoicedrivenSceneGroupOperation,
} from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    getVoiceDrivenBaseAssetCutRegions,
    getVoiceDrivenBaseAssetSourceDuration,
} from 'js/src/luminary/luminary-selectors';

export const trimSceneGroupSchema = z.object({
    scene_group_id: z
        .string()
        .describe(
            'The stable scene_group_id of the voiceover or talking-head scene group to trim (from the video state).',
        ),
    words: z
        .array(z.string())
        .optional()
        .describe(
            'The exact consecutive transcript words of the section to KEEP, in order as spelled in the transcript. Everything outside this span is discarded and the video gets shorter. The kept section starts and ends on a word boundary. For a long section, pass start_quote and end_quote instead.',
        ),
    start_quote: z
        .string()
        .optional()
        .describe(
            'Instead of words: the first few words of the section to keep, copied exactly as the transcript spells them. Quote enough words that they occur only once in the recording. Pass with end_quote.',
        ),
    end_quote: z
        .string()
        .optional()
        .describe(
            'Instead of words: the last few words of the section to keep, copied exactly as the transcript spells them. Pass with start_quote.',
        ),
});
export type TrimSceneGroupArgs = z.infer<typeof trimSceneGroupSchema>;

type TimedWord = { word: string; startTime: number; endTime: number };

/**
 * Where the requested words sit in the full transcript. The match steps over words that are
 * already cut, since the agent reads the transcript without them; those stay cut.
 */
function findRequestedWords(
    words: readonly TimedWord[],
    requestedWords: string[],
    isCut: (word: TimedWord) => boolean,
): { startIndex: number; endIndex: number; skippedIndices: number[] } | null {
    const request = requestedWords
        .flatMap((word) => word.split(/\s+/))
        .map(normalizeTranscriptToken)
        .filter((token) => token !== '');
    if (request.length === 0) {
        return null;
    }
    const normalized = words.map((word) => normalizeTranscriptToken(word.word));

    for (let startIndex = 0; startIndex < words.length; startIndex++) {
        if (normalized[startIndex] !== request[0]) {
            continue;
        }
        const skippedIndices: number[] = [];
        let endIndex = startIndex;
        let requestIndex = 1;
        for (let i = startIndex + 1; i < words.length && requestIndex < request.length; i++) {
            if (normalized[i] === request[requestIndex]) {
                endIndex = i;
                requestIndex++;
            } else if (isCut(words[i])) {
                skippedIndices.push(i);
            } else if (normalized[i] !== '') {
                break;
            }
        }
        if (requestIndex === request.length) {
            return { startIndex, endIndex, skippedIndices };
        }
    }
    return null;
}

/** The kept span as playback regions, minus the given cuts inside it, sorted by start. */
function keptSpanWithoutCuts(
    cuts: [number, number][],
    [keptStartMs, keptEndMs]: [number, number],
): [number, number][] {
    const playbackRegions: [number, number][] = [];
    let regionStart = keptStartMs;
    for (const [cutStart, cutEnd] of cuts) {
        if (cutEnd <= regionStart || cutStart >= keptEndMs) {
            continue;
        }
        if (cutStart > regionStart) {
            playbackRegions.push([regionStart, cutStart]);
        }
        regionStart = Math.max(regionStart, cutEnd);
    }
    if (regionStart < keptEndMs) {
        playbackRegions.push([regionStart, keptEndMs]);
    }
    return playbackRegions;
}

/** Each run of stepped-over words as a cut, from the kept word before it to the one after. */
function cutsForSkippedWords(
    words: readonly TimedWord[],
    skippedIndices: number[],
): [number, number][] {
    const cuts: [number, number][] = [];
    for (let i = 0; i < skippedIndices.length; i++) {
        const runStart = skippedIndices[i];
        while (skippedIndices[i + 1] === skippedIndices[i] + 1) {
            i++;
        }
        const runEnd = skippedIndices[i];
        cuts.push([words[runStart - 1].endTime, words[runEnd + 1].startTime]);
    }
    return cuts;
}

export class TrimSceneGroupTool extends VideoStateTool<typeof trimSceneGroupSchema> {
    protected override readonly schema = trimSceneGroupSchema;
    protected override readonly toolName = TOOL_NAMES.trimSceneGroup;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription = `Shorten an existing voiceover or talking-head scene group to a single section of its recording: keep the given consecutive transcript words and discard the rest, so the video gets shorter. Choose ${TOOL_NAMES.trimSceneGroup} when the user wants only a clip or moment of a recording (e.g. a section for social). (To divide the narration into two independently-editable parts while keeping all the footage, use ${TOOL_NAMES.splitSceneGroup}.)`;

    public override getToolDisplayName(): string {
        return 'Trimming scene group';
    }

    public get statusIcon(): string {
        return 'content_cut';
    }

    public async execute(args: TrimSceneGroupArgs, context: ToolExecutionContext) {
        const { words: requestedWords, start_quote, end_quote } = args;
        const { parentActionGroup } = context;

        const quotes =
            start_quote !== undefined && end_quote !== undefined
                ? { start_quote, end_quote }
                : null;
        if (requestedWords && quotes) {
            return `Error: pass either "words" or start_quote and end_quote, not both.`;
        }
        if (!quotes && (start_quote !== undefined || end_quote !== undefined)) {
            return `Error: start_quote and end_quote go together. Pass both, or pass "words" instead.`;
        }
        if (!quotes && (!requestedWords || requestedWords.length === 0)) {
            return `Error: "words" was empty. Pass the transcript words of the section to keep, or start_quote and end_quote.`;
        }
        const sectionLabel = quotes
            ? `"${quotes.start_quote} … ${quotes.end_quote}"`
            : `"${(requestedWords ?? []).join(' ')}"`;

        const { sceneGroup } = resolveSceneGroupHandle(this.luminary, args);

        // Match against the group's OWN transcript, so the cut lands on the same words the agent
        // read — no timestamps to drift. originalTranscript word times reflect any user edits.
        const transcript = sceneGroup.originalTranscript;
        const transcriptWords = transcript?.transcriptData?.words || transcript?.words || [];
        if (!transcript || transcriptWords.length === 0) {
            return `Error: Scene group ${sceneGroup.id} does not have a transcript. Only voiceover and talking-head scene groups can be trimmed.`;
        }

        const allCuts = getVoiceDrivenBaseAssetCutRegions(sceneGroup);
        const isCut = (word: TimedWord) => {
            const midpoint = (word.startTime + word.endTime) / 2;
            return allCuts.some(([cutStart, cutEnd]) => midpoint >= cutStart && midpoint <= cutEnd);
        };
        // The shared matcher still covers what the step-over one doesn't, like a phrase in a
        // language written without spaces.
        const fallbackMatch = () => {
            const exactMatch = findWordSequenceIndices(transcriptWords, requestedWords ?? []);
            return exactMatch && { ...exactMatch, skippedIndices: [] };
        };
        let match: { startIndex: number; endIndex: number; skippedIndices: number[] } | null;
        if (quotes) {
            const span = locateQuotedSpan(transcriptWords, quotes);
            if (!span.ok) {
                return `Error: ${span.error}, so there is no section to keep. Copy the quotes exactly as the transcript spells them.`;
            }
            match = { startIndex: span.startIndex, endIndex: span.endIndex, skippedIndices: [] };
        } else {
            match =
                findRequestedWords(transcriptWords, requestedWords ?? [], isCut) ?? fallbackMatch();
        }
        if (!match) {
            return `Error: ${sectionLabel} does not appear in this recording's transcript, so there is no section to keep. Copy the words exactly as they appear in the transcript, or pass start_quote and end_quote instead.`;
        }
        const keptWordSpan = wordSpanToClipRegion(transcriptWords, match);

        // A cut from the very start or to the very end is an earlier trim, which this one
        // replaces. The cuts inside the recording (removed fillers or pauses, a manual cut) stay,
        // as do already-cut words the agent left out, wherever an earlier trim folded them.
        const sourceDurationMs = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        const cutsToKeep = [
            ...allCuts.filter(([cutStart, cutEnd]) => cutStart > 0 && cutEnd < sourceDurationMs),
            ...cutsForSkippedWords(transcriptWords, match.skippedIndices),
        ].sort(([a], [b]) => a - b);

        const desiredCuts = VideoData.getCutRegions(
            keptSpanWithoutCuts(cutsToKeep, [keptWordSpan.startTimeMs, keptWordSpan.endTimeMs]),
            sourceDurationMs,
        ).filter(([cutStart, cutEnd]) => cutEnd > cutStart);
        const isSameCut = ([aStart, aEnd]: [number, number], [bStart, bEnd]: [number, number]) =>
            aStart === bStart && aEnd === bEnd;
        const cutsToUndo = allCuts.filter((cut) => !desiredCuts.some((d) => isSameCut(cut, d)));
        const cutsToMake = desiredCuts.filter((cut) => !allCuts.some((c) => isSameCut(cut, c)));

        const { cutVoicedrivenSceneGroup, uncutVoicedrivenSceneGroup } = makeOperationsCallable(
            { cutVoicedrivenSceneGroupOperation, uncutVoicedrivenSceneGroupOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );

        try {
            // Undo and redo only the cuts that change, through the operations that move every
            // scene in the group, so the scenes laid out over the narration keep their words.
            for (const cut of cutsToUndo) {
                uncutVoicedrivenSceneGroup(sceneGroup.id, cut, true, parentActionGroup);
            }
            for (const [cutStart, cutEnd] of cutsToMake) {
                cutVoicedrivenSceneGroup(sceneGroup.id, cutStart, cutEnd, true, parentActionGroup);
            }

            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup,
                }),
            );

            const shortId =
                computeSceneGroupShortIds(this.luminary.sceneGroups.map((group) => group.id)).get(
                    sceneGroup.id,
                ) ?? sceneGroup.id;
            return `Success: Trimmed scene group ${shortId} to the section ${sectionLabel} (${Math.round(keptWordSpan.startTimeMs)}ms–${Math.round(keptWordSpan.endTimeMs)}ms of the recording); the rest of the recording was discarded.`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error trimming scene group: ${errorMessage}`;
        }
    }
}
